import { Injectable } from '@angular/core';
import { WidgetHandoffStatus } from '@finxone-platform/shared/app-sdk';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';
import { WidgetHandoffEntry, WidgetInstanceScope } from './widget-handoff.type';

/**
 * The route a hand-off's redirectUri points back at (registered in
 * app-routing.module.ts). It carries `?handoffId=` — the only correlation that
 * survives leaving the origin.
 */
export const WIDGET_HANDOFF_RETURN_PATH = '/widget-handoff-return';

/** Query param the return route resolves the hand-off from. */
export const WIDGET_HANDOFF_ID_PARAM = 'handoffId';

/**
 * Correlation store behind sdk.handoff.* and sdk.state.*.
 *
 * A hand-off (PISP deposit, AISP link, KYC session) usually destroys the page:
 * control leaves the origin and comes back to a fresh document, so neither the
 * widget's promise nor its JS state survives. This keeps both across the round
 * trip in sessionStorage — the same lifetime as the token already there, so a
 * hand-off dies with the tab and can never be resumed in another one.
 *
 * It is also the push channel for the case where the widget *does* survive (a
 * transport that never navigated, or a hand-off that fails before control ever
 * left). Every mutation bumps `revision`, so `records$(scope)` re-emits this
 * instance's records and the 'handoffs' data topic pushes them to the live
 * widget over its port. Nothing else has to be wired for that: the bridge in
 * custom-widget-handoff.ts subscribes records$ as the topic's subscribe
 * handler, and withHandoffsTopic hands that to the app-sdk host core, which
 * pushes it like any other data topic.
 *
 * Instance scoping is the security property, because sdk.state carries no
 * capability grant and the 'handoffs' topic grant is per-widget, not per-record.
 * It is enforced twice: every key is namespaced by widgetId + instanceId, and
 * every read filters the decoded rows by the same pair, so a tampered or
 * migrated storage key still cannot leak another instance's records or state.
 */
@Injectable({ providedIn: 'root' })
export class WidgetHandoffService {
  /** Records for one instance: 'fx.widget.handoffs.<widgetId>.<instanceId>'. */
  private static readonly RECORDS_PREFIX = 'fx.widget.handoffs.';
  /** Scratchpad for one instance: 'fx.widget.state.<widgetId>.<instanceId>'. */
  private static readonly STATE_PREFIX = 'fx.widget.state.';
  /**
   * handoffId -> owning instance. The return route arrives with only the
   * handoffId (it is a query param on the redirectUri), so it needs this to
   * find which instance's records to write the outcome onto.
   */
  private static readonly INDEX_KEY = 'fx.widget.handoffs.index';

  private readonly revision = new BehaviorSubject<number>(0);

  /**
   * Emits this instance's records now and after every change that affects them.
   * `revision` is store-wide, so the emissions are de-duplicated per scope —
   * one instance completing a hand-off must not push a redundant identical
   * payload down every other instance's port.
   */
  public records$(scope: WidgetInstanceScope): Observable<WidgetHandoffEntry[]> {
    return this.revision.pipe(
      map(() => this.list(scope)),
      distinctUntilChanged((previous, next) => JSON.stringify(previous) === JSON.stringify(next)),
    );
  }

  public list(scope: WidgetInstanceScope): WidgetHandoffEntry[] {
    return this.read<WidgetHandoffEntry[]>(this.recordsKey(scope), []).filter(
      (entry) => entry.widgetId === scope.widgetId && entry.instanceId === scope.instanceId,
    );
  }

  /**
   * Persists a 'pending' hand-off before any provider call is made, so a
   * redirect that commits before the response is processed still leaves a
   * record the return route can resolve.
   */
  public start(
    scope: WidgetInstanceScope,
    input: { kind: string; params: Record<string, unknown>; returnUrl: string },
  ): WidgetHandoffEntry {
    const entry: WidgetHandoffEntry = {
      handoffId: this.newHandoffId(),
      widgetId: scope.widgetId,
      instanceId: scope.instanceId,
      kind: input.kind,
      params: input.params,
      status: 'pending',
      returnUrl: input.returnUrl,
      startedAt: new Date().toISOString(),
    };
    const entries = this.list(scope);
    entries.push(entry);
    this.write(this.recordsKey(scope), entries);
    const index = this.read<Record<string, WidgetInstanceScope>>(WidgetHandoffService.INDEX_KEY, {});
    index[entry.handoffId] = { widgetId: scope.widgetId, instanceId: scope.instanceId };
    this.write(WidgetHandoffService.INDEX_KEY, index);
    this.revision.next(this.revision.value + 1);
    return entry;
  }

  /**
   * Resolves a hand-off by id across instances — for the return route only,
   * which has no widget context. Widget-facing reads go through list().
   */
  public find(handoffId: string): WidgetHandoffEntry | undefined {
    const scope = this.read<Record<string, WidgetInstanceScope>>(WidgetHandoffService.INDEX_KEY, {})[
      handoffId
    ];
    if (!scope) return undefined;
    return this.list(scope).find((entry) => entry.handoffId === handoffId);
  }

  public setCorrelation(handoffId: string, correlation: Record<string, unknown>): void {
    this.patch(handoffId, (entry) => ({ ...entry, correlation: { ...entry.correlation, ...correlation } }));
  }

  public complete(handoffId: string, result: Record<string, unknown>): WidgetHandoffEntry | undefined {
    return this.patch(handoffId, (entry) => ({ ...entry, status: 'completed' as WidgetHandoffStatus, result }));
  }

  public fail(handoffId: string, error: string): WidgetHandoffEntry | undefined {
    return this.patch(handoffId, (entry) => ({ ...entry, status: 'failed' as WidgetHandoffStatus, error }));
  }

  public getState<T>(scope: WidgetInstanceScope): T | undefined {
    return this.read<T | undefined>(this.stateKey(scope), undefined);
  }

  public setState(scope: WidgetInstanceScope, value: unknown): void {
    this.write(this.stateKey(scope), value);
  }

  /** Drops everything held for one instance (records, index rows, state). */
  public clearInstance(scope: WidgetInstanceScope): void {
    const index = this.read<Record<string, WidgetInstanceScope>>(WidgetHandoffService.INDEX_KEY, {});
    for (const entry of this.list(scope)) delete index[entry.handoffId];
    this.write(WidgetHandoffService.INDEX_KEY, index);
    this.remove(this.recordsKey(scope));
    this.remove(this.stateKey(scope));
    this.revision.next(this.revision.value + 1);
  }

  private patch(
    handoffId: string,
    update: (entry: WidgetHandoffEntry) => WidgetHandoffEntry,
  ): WidgetHandoffEntry | undefined {
    const existing = this.find(handoffId);
    if (!existing) return undefined;
    const scope = { widgetId: existing.widgetId, instanceId: existing.instanceId };
    const updated = update(existing);
    this.write(
      this.recordsKey(scope),
      this.list(scope).map((entry) => (entry.handoffId === handoffId ? updated : entry)),
    );
    this.revision.next(this.revision.value + 1);
    return updated;
  }

  private recordsKey(scope: WidgetInstanceScope): string {
    return WidgetHandoffService.RECORDS_PREFIX + this.scopeKey(scope);
  }

  private stateKey(scope: WidgetInstanceScope): string {
    return WidgetHandoffService.STATE_PREFIX + this.scopeKey(scope);
  }

  /** Encoded so an id containing a separator cannot address another scope. */
  private scopeKey(scope: WidgetInstanceScope): string {
    return `${encodeURIComponent(scope.widgetId)}.${encodeURIComponent(scope.instanceId)}`;
  }

  private newHandoffId(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private read<T>(key: string, fallback: T): T {
    try {
      const raw = sessionStorage.getItem(key);
      return raw === null ? fallback : (JSON.parse(raw) as T);
    } catch {
      // Unavailable or corrupted storage must not break the widget; the
      // hand-off simply cannot be correlated on the way back.
      return fallback;
    }
  }

  private write(key: string, value: unknown): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore storage errors (private mode, quota)
    }
  }

  private remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore storage errors
    }
  }
}
